import { storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

class ImageService {
  // Upload image to Firestore Storage
  async uploadImage(file, path = 'properties') {
    try {
      // Generate unique filename
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 15);
      const fileName = `${timestamp}_${randomString}_${file.name}`;
      
      // Create storage reference
      const storageRef = ref(storage, `${path}/${fileName}`);
      
      // Upload file
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        path: `${path}/${fileName}`,
        name: fileName
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Failed to upload image');
    }
  }

  // Upload multiple images
  async uploadImages(files, path = 'properties') {
    try {
      const uploadPromises = files.map(file => this.uploadImage(file, path));
      const results = await Promise.all(uploadPromises);
      return results;
    } catch (error) {
      console.error('Error uploading images:', error);
      throw new Error('Failed to upload images');
    }
  }

  // Delete image from storage
  async deleteImage(imagePath) {
    try {
      const imageRef = ref(storage, imagePath);
      await deleteObject(imageRef);
      return true;
    } catch (error) {
      console.error('Error deleting image:', error);
      throw new Error('Failed to delete image');
    }
  }

  // Delete multiple images
  async deleteImages(imagePaths) {
    try {
      const deletePromises = imagePaths.map(path => this.deleteImage(path));
      await Promise.all(deletePromises);
      return true;
    } catch (error) {
      console.error('Error deleting images:', error);
      throw new Error('Failed to delete images');
    }
  }

  // Get optimized image URL (for different sizes)
  getOptimizedImageUrl(imageUrl, width = 800, height = 600) {
    if (!imageUrl) return null;
    
    // Firebase Storage URLs should be used directly without query parameters
    // Firebase Storage doesn't support URL-based resizing like Cloudinary
    // If resizing is needed, it should be done client-side or via Cloud Functions
    if (imageUrl.includes('firebasestorage.googleapis.com')) {
      // Return the URL as-is - Firebase Storage URLs are already optimized
      return imageUrl;
    }
    
    // For other URLs (Cloudinary, etc.), return as-is
    return imageUrl;
  }

  // Get thumbnail URL
  getThumbnailUrl(imageUrl, size = 200) {
    return this.getOptimizedImageUrl(imageUrl, size, size);
  }
}

export default new ImageService();
